import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function ApiDocs() {
  const { user } = useApp();
  const { t } = useI18n();
  const [apiKey, setApiKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Load saved API key
    const saved = localStorage.getItem('api_key');
    if (saved) setApiKey(saved);
  }, []);

  function generateApiKey() {
    setGenerating(true);
    // Generate a random API key
    const key = 'wpl_' + Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    setTimeout(() => {
      setApiKey(key);
      localStorage.setItem('api_key', key);
      setGenerating(false);
    }, 1000);
  }

  function copyKey() {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const endpoints = [
    {
      method: 'GET',
      path: '/api/v1/me',
      desc: 'Получить данные текущего пользователя',
    },
    {
      method: 'GET',
      path: '/api/v1/balance',
      desc: 'Получить баланс во всех валютах',
    },
    {
      method: 'GET',
      path: '/api/v1/invoices',
      desc: 'Список фактур',
    },
    {
      method: 'POST',
      path: '/api/v1/invoices',
      desc: 'Создать новую фактуру',
    },
    {
      method: 'GET',
      path: '/api/v1/transactions',
      desc: 'История транзакций',
    },
    {
      method: 'POST',
      path: '/api/v1/payment-links',
      desc: 'Создать ссылку на оплату',
    },
    {
      method: 'GET',
      path: '/api/v1/contracts',
      desc: 'Список договоров',
    },
    {
      method: 'POST',
      path: '/api/v1/webhooks',
      desc: 'Настроить webhook уведомления',
    },
  ];

  const methodColors = {
    GET: '#10b981',
    POST: '#3b82f6',
    PUT: '#f59e0b',
    DELETE: '#ef4444',
  };

  return (
    <div className="page">
      <BackButton />
      <h1>🔌 API</h1>

      {/* API Key Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: 12,
        padding: 20,
        marginBottom: 24,
        border: '1px solid rgba(99,102,241,0.3)',
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>🔑 API Ключ</div>
        
        {!apiKey ? (
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={generateApiKey}
            disabled={generating}
          >
            {generating ? '⏳ Генерация...' : '🔑 Сгенерировать API ключ'}
          </button>
        ) : (
          <>
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              borderRadius: 8,
              padding: 12,
              fontFamily: 'monospace',
              fontSize: 12,
              wordBreak: 'break-all',
              marginBottom: 12,
            }}>
              {showKey ? apiKey : '•'.repeat(48)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-small"
                style={{ flex: 1, background: 'rgba(255,255,255,0.1)' }}
                onClick={() => setShowKey(!showKey)}
              >
                {showKey ? '🙈 Скрыть' : '👁 Показать'}
              </button>
              <button
                className="btn btn-small"
                style={{ flex: 1, background: 'rgba(255,255,255,0.1)' }}
                onClick={copyKey}
              >
                {copied ? '✅ Скопировано!' : '📋 Копировать'}
              </button>
            </div>
            <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>
              ⚠️ Храните ключ в безопасности. Не передавайте его третьим лицам.
            </div>
          </>
        )}
      </div>

      {/* Base URL */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🌐 Base URL</div>
        <code style={{
          background: 'rgba(0,0,0,0.3)',
          padding: '8px 12px',
          borderRadius: 6,
          display: 'block',
          fontSize: 13,
        }}>
          https://api.workifypl.com/api/v1
        </code>
      </div>

      {/* Authentication */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🔐 Аутентификация</div>
        <p style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>
          Добавьте заголовок к каждому запросу:
        </p>
        <code style={{
          background: 'rgba(0,0,0,0.3)',
          padding: '8px 12px',
          borderRadius: 6,
          display: 'block',
          fontSize: 12,
        }}>
          Authorization: Bearer {apiKey || 'YOUR_API_KEY'}
        </code>
      </div>

      {/* Endpoints */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>📚 Endpoints</div>
        {endpoints.map((ep, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 8,
            padding: 12,
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{
              background: methodColors[ep.method],
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 'bold',
              minWidth: 50,
              textAlign: 'center',
            }}>
              {ep.method}
            </span>
            <div style={{ flex: 1 }}>
              <code style={{ fontSize: 13 }}>{ep.path}</code>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{ep.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Example */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 12,
        padding: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>💻 Пример запроса (cURL)</div>
        <pre style={{
          background: 'rgba(0,0,0,0.3)',
          padding: 12,
          borderRadius: 6,
          fontSize: 11,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
        }}>
{`curl -X GET "https://api.workifypl.com/api/v1/balance" \\
  -H "Authorization: Bearer ${apiKey || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json"`}
        </pre>
      </div>

      {/* Webhooks */}
      <div style={{
        background: 'rgba(99,102,241,0.1)',
        borderRadius: 12,
        padding: 16,
        marginTop: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🔔 Webhooks</div>
        <p style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>
          Получайте уведомления о событиях в реальном времени:
        </p>
        <ul style={{ fontSize: 12, paddingLeft: 20, opacity: 0.8 }}>
          <li>invoice.paid — Оплата фактуры</li>
          <li>payment.received — Получен платёж</li>
          <li>withdrawal.approved — Вывод одобрен</li>
          <li>contract.signed — Договор подписан</li>
        </ul>
      </div>
    </div>
  );
}
